import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type Project } from '../api';
import { useAuth } from '../auth';
import { Badge, Button, Page } from '../ui';

export function ProjectSettings() {
  const { slug } = useParams<{ slug: string }>();
  const nav = useNavigate();
  const { me } = useAuth();
  const [project, setProject] = useState<Project | null>(null);
  const [keys, setKeys] = useState<Array<{ id: string; label: string; active: boolean; masked: string; createdAt: string; revokedAt: string | null }>>([]);
  const [newLabel, setNewLabel] = useState('web');
  const [justIssued, setJustIssued] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () => {
    if (!slug) return;
    api
      .getProject(slug)
      .then(({ project }) => setProject(project))
      .catch((e) => setErr(e.status === 403 ? 'Sem permissão' : 'Falha'));
    api
      .listKeys(slug)
      .then(({ keys }) => setKeys(keys))
      .catch((e) => setErr(e.status === 403 ? 'Sem permissão' : 'Falha'));
  };

  useEffect(load, [slug]);

  async function issue() {
    if (!slug) return;
    const { key } = await api.issueKey(slug, newLabel);
    setJustIssued(key.publicKey);
    setNewLabel('web');
    load();
  }

  async function revoke(id: string) {
    if (!slug) return;
    if (!confirm('Revogar esta chave? Clientes usando-a pararão de enviar.')) return;
    await api.revokeKey(slug, id);
    load();
  }

  async function removeProject() {
    if (!slug || !project) return;
    if (!confirm(`Excluir o projeto "${project.name}"? Isto apaga documentos, agentes e tarefas. NÃO pode ser desfeito.`)) return;
    try {
      await api.deleteProject(slug);
      nav('/');
    } catch (e) {
      setErr((e as { error?: string }).error ?? 'Falha ao excluir');
    }
  }

  if (!slug) return null;

  const isAdmin = me?.globalRole === 'admin';

  return (
    <Page title="Configurações" subtitle="Configurar o projeto, integração com GitHub e chaves do SDK.">
      <div className="settings">
        {isAdmin && project && (
          <section>
            <h2>Configuração do projeto</h2>
            <EditProjectForm
              project={project}
              onSaved={(updated) => setProject(updated)}
            />
          </section>
        )}

        <section>
          <h2>Chaves públicas do SDK</h2>
          {err && <div className="ui-error-banner">{err}</div>}
          {justIssued && (
            <div style={{ marginBottom: 12 }}>
              <p style={{ color: 'var(--color-ok)' }}>
                Chave criada. <strong>Copie agora — não será exibida de novo.</strong>
              </p>
              <div className="revealed-key">{justIssued}</div>
            </div>
          )}
          <div className="inline-form" style={{ marginBottom: 12 }}>
            <input
              placeholder="label (ex: web, mobile)"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
            />
            <Button type="button" variant="primary" size="sm" onClick={issue}>Emitir chave</Button>
          </div>
          <table>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>label</th>
                <th style={{ textAlign: 'left' }}>chave</th>
                <th style={{ textAlign: 'left' }}>status</th>
                <th style={{ textAlign: 'left' }}>criada</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id}>
                  <td>{k.label}</td>
                  <td><code>{k.masked}</code></td>
                  <td>
                    <Badge variant={k.active ? 'ok' : 'neutral'}>{k.active ? 'ativa' : 'revogada'}</Badge>
                  </td>
                  <td>{new Date(k.createdAt).toLocaleDateString()}</td>
                  <td>
                    {k.active && (
                      <Button type="button" variant="link" size="sm" onClick={() => revoke(k.id)}>
                        revogar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {isAdmin && (
          <section>
            <h2 style={{ color: '#dc2626' }}>Zona de perigo</h2>
            <p style={{ color: 'var(--color-fg-muted)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-3)' }}>
              Excluir o projeto remove permanentemente seus documentos, agentes, tarefas e chaves. Não pode ser desfeito.
            </p>
            <Button type="button" variant="danger" onClick={removeProject}>
              Excluir projeto
            </Button>
          </section>
        )}
      </div>
    </Page>
  );
}

function EditProjectForm({
  project,
  onSaved,
}: {
  project: Project;
  onSaved: (p: Project) => void;
}) {
  const [name, setName] = useState(project.name);
  const [repoUrl, setRepoUrl] = useState(project.repoUrl);
  const [baseBranch, setBaseBranch] = useState(project.baseBranch);
  const [localRepoPath, setLocalRepoPath] = useState(project.localRepoPath ?? '');
  const [premisesMd, setPremisesMd] = useState(project.premisesMd);
  const [configYaml, setConfigYaml] = useState(project.configYaml);
  const [githubTokenNew, setGithubTokenNew] = useState('');
  const [githubActor, setGithubActor] = useState(project.githubActor ?? '');
  const [bddFramework, setBddFramework] = useState(project.bddFramework);
  const [featuresDir, setFeaturesDir] = useState(project.featuresDir);
  const [stepsDir, setStepsDir] = useState(project.stepsDir);
  const [testCommand, setTestCommand] = useState(project.testCommand);
  const [testBaseUrl, setTestBaseUrl] = useState(project.testBaseUrl ?? '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const lrp = localRepoPath.trim();
      const tk = githubTokenNew.trim();
      const act = githubActor.trim();
      const tbu = testBaseUrl.trim();
      const { project: updated } = await api.updateProject(project.slug, {
        name: name.trim(),
        repoUrl: repoUrl.trim(),
        baseBranch: baseBranch.trim(),
        premisesMd,
        configYaml,
        ...(lrp ? { localRepoPath: lrp } : {}),
        // Só manda token novo se usuário digitou — senão mantém o que tem.
        ...(tk ? { githubToken: tk } : {}),
        githubActor: act,
        bddFramework: bddFramework.trim(),
        featuresDir: featuresDir.trim(),
        stepsDir: stepsDir.trim(),
        testCommand: testCommand.trim(),
        ...(tbu ? { testBaseUrl: tbu } : {}),
      });
      setGithubTokenNew('');  // limpa o campo após salvar
      onSaved(updated);
      setMsg({ kind: 'ok', text: 'Salvo.' });
    } catch (e: unknown) {
      const err = e as { error?: string; status?: number };
      setMsg({ kind: 'err', text: err.error ?? `Falha (${err.status ?? '?'})` });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="project-form" style={{ padding: 0, border: 0, background: 'transparent', marginBottom: 0 }}>
      <div className="project-form-row">
        <label>
          <span>slug (imutável)</span>
          <input value={project.slug} disabled />
        </label>
        <label>
          <span>Nome</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required />
        </label>
      </div>
      <div className="project-form-row">
        <label>
          <span>Repo URL</span>
          <input value={repoUrl} onChange={(e) => setRepoUrl(e.target.value)} required />
        </label>
        <label>
          <span>Base branch</span>
          <input value={baseBranch} onChange={(e) => setBaseBranch(e.target.value)} required />
        </label>
      </div>
      <label>
        <span>Caminho local do repo</span>
        <input value={localRepoPath} onChange={(e) => setLocalRepoPath(e.target.value)} placeholder="E:/dev/mistik.island" />
      </label>
      <label>
        <span>Premises (markdown)</span>
        <textarea rows={4} value={premisesMd} onChange={(e) => setPremisesMd(e.target.value)} />
      </label>
      <label>
        <span>config.yaml</span>
        <textarea rows={4} value={configYaml} onChange={(e) => setConfigYaml(e.target.value)} />
      </label>
      <div className="project-form-row">
        <label>
          <span>
            GitHub PAT {project.githubToken ? '(definido ✓ — digite pra trocar)' : '(vazio = usa do container)'}
          </span>
          <input
            type="password"
            autoComplete="off"
            placeholder={project.githubToken ? '••••••••••••' : 'github_pat_... ou ghp_...'}
            value={githubTokenNew}
            onChange={(e) => setGithubTokenNew(e.target.value)}
          />
        </label>
        <label>
          <span>GitHub actor (display)</span>
          <input
            placeholder="zighue"
            value={githubActor}
            onChange={(e) => setGithubActor(e.target.value)}
          />
        </label>
      </div>
      <fieldset style={{ border: '1px solid var(--border)', padding: 12, marginTop: 8 }}>
        <legend style={{ fontSize: 12, color: 'var(--muted)', padding: '0 6px' }}>
          Testes BDD (cucumber-js)
        </legend>
        <div className="project-form-row">
          <label>
            <span>Framework</span>
            <input value={bddFramework} onChange={(e) => setBddFramework(e.target.value)} />
          </label>
          <label>
            <span>Comando de teste</span>
            <input
              value={testCommand}
              onChange={(e) => setTestCommand(e.target.value)}
              placeholder="pnpm test:bdd"
            />
          </label>
        </div>
        <div className="project-form-row">
          <label>
            <span>Pasta de features</span>
            <input
              value={featuresDir}
              onChange={(e) => setFeaturesDir(e.target.value)}
              placeholder="features"
            />
          </label>
          <label>
            <span>Pasta de step definitions</span>
            <input
              value={stepsDir}
              onChange={(e) => setStepsDir(e.target.value)}
              placeholder="features/step_definitions"
            />
          </label>
        </div>
        <label>
          <span>URL do ambiente perene de testes (TEST_BASE_URL)</span>
          <input
            value={testBaseUrl}
            onChange={(e) => setTestBaseUrl(e.target.value)}
            placeholder="https://test.mistik.island"
          />
        </label>
      </fieldset>
      {msg && (
        <div style={{ color: msg.kind === 'ok' ? 'var(--ok)' : 'var(--bug)', fontSize: 13 }}>
          {msg.text}
        </div>
      )}
      <div className="inline-form">
        <button type="submit" disabled={busy}>
          {busy ? 'Salvando…' : 'Salvar alterações'}
        </button>
      </div>
    </form>
  );
}
